import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Table, Badge, Spinner } from 'react-bootstrap';
import { 
  FiBriefcase, 
  FiCalendar, 
  FiCheckSquare, 
  FiUsers,
  FiClock
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import adminService, { DashboardStats } from '../services/admin.service';
import casesService, { Case } from '../services/cases.service';
import tasksService, { Task } from '../services/tasks.service';
import hearingsService, { Hearing } from '../services/hearings.service';
import { useAuth } from '../context/AuthContext';

interface StatCard {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  link: string;
  subtitle?: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [recentCases, setRecentCases] = useState<Case[]>([]);
  const [pendingTasks, setPendingTasks] = useState<Task[]>([]);
  const [upcomingHearings, setUpcomingHearings] = useState<Hearing[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatCard[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Cargar cada cosa independientemente para que si una falla, las demás sigan
      let dashboardStats = { cases: { total: 0, active: 0, suspended: 0, closed: 0, urgent: 0 }, tasks: { total: 0, pending: 0, in_progress: 0, completed: 0, overdue: 0 }, hearings: { total: 0, this_week: 0, today: 0 }, users: { active: 0, total: 0 } };
      
      try {
        dashboardStats = await adminService.getDashboardStats();
      } catch (e) {
        console.error('Error loading dashboard stats:', e);
      }

      try {
        const casesResponse = await casesService.list({ limit: 5 });
        setRecentCases(casesResponse.data);
      } catch (e) {
        console.error('Error loading cases:', e);
      }

      try {
        const tasksResponse = await tasksService.list({ status: 'pending', limit: 5 });
        setPendingTasks(tasksResponse.data);
      } catch (e) {
        console.error('Error loading tasks:', e);
      }

      try {
        const hearingsResponse = await hearingsService.getUpcoming(7);
        setUpcomingHearings(hearingsResponse.slice(0, 5));
      } catch (e) {
        console.error('Error loading hearings:', e);
      }

      // Convertir los valores a números enteros
      const casesActive = parseInt(String(dashboardStats.cases.active || 0), 10);
      const casesUrgent = parseInt(String(dashboardStats.cases.urgent || 0), 10);
      const tasksPending = parseInt(String(dashboardStats.tasks.pending || 0), 10);
      const tasksOverdue = parseInt(String(dashboardStats.tasks.overdue || 0), 10);
      const hearingsThisWeek = parseInt(String(dashboardStats.hearings.this_week || 0), 10);
      const hearingsToday = parseInt(String(dashboardStats.hearings.today || 0), 10);
      const usersActive = parseInt(String(dashboardStats.users.active || 0), 10);
      const usersTotal = parseInt(String(dashboardStats.users.total || 0), 10);

      // Verificar si es admin o coordinador
      const userRoles = user?.roles || [];
      const isAdmin = userRoles.includes('super_admin') || userRoles.includes('coordinador');

      const statCards: StatCard[] = [
        {
          title: 'Casos Activos',
          value: casesActive,
          icon: <FiBriefcase size={24} />,
          color: 'primary',
          link: '/cases',
          subtitle: `${casesUrgent} urgentes`
        },
        {
          title: 'Tareas Pendientes',
          value: tasksPending,
          icon: <FiCheckSquare size={24} />,
          color: 'warning',
          link: '/tasks',
          subtitle: `${tasksOverdue} vencidas`
        },
        {
          title: 'Audiencias Esta Semana',
          value: hearingsThisWeek,
          icon: <FiCalendar size={24} />,
          color: 'info',
          link: '/hearings',
          subtitle: `${hearingsToday} hoy`
        },
      ];

      // Solo mostrar usuarios a admins
      if (isAdmin) {
        statCards.push({
          title: 'Usuarios Activos',
          value: usersActive,
          icon: <FiUsers size={24} />,
          color: 'success',
          link: '/users',
          subtitle: `${usersTotal} total`
        });
      }
      
      setStats(statCards);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setStats([
        { title: 'Casos Activos', value: 0, icon: <FiBriefcase size={24} />, color: 'primary', link: '/cases' },
        { title: 'Tareas Pendientes', value: 0, icon: <FiCheckSquare size={24} />, color: 'warning', link: '/tasks' },
        { title: 'Audiencias Esta Semana', value: 0, icon: <FiCalendar size={24} />, color: 'info', link: '/hearings' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getSemaphoreBadge = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'success',
      yellow: 'warning',
      red: 'danger'
    };
    return <Badge bg={colorMap[color] || 'secondary'}>●</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { bg: string; label: string }> = {
      open: { bg: 'info', label: 'Abierto' },
      in_progress: { bg: 'warning', label: 'En Proceso' },
      closed: { bg: 'success', label: 'Cerrado' },
      suspended: { bg: 'secondary', label: 'Suspendido' }
    };
    const config = statusMap[status] || { bg: 'secondary', label: status };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, string> = {
      urgent: 'danger',
      high: 'warning',
      normal: 'info',
      low: 'secondary'
    };
    const labels: Record<string, string> = {
      urgent: 'Urgente',
      high: 'Alta',
      normal: 'Normal',
      low: 'Baja'
    };
    return <Badge bg={variants[priority] || 'secondary'}>{labels[priority] || priority}</Badge>;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', { 
      day: '2-digit', 
      month: 'short' 
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header mb-4">
        <h4 className="page-title">Dashboard</h4>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><Link to="/">Inicio</Link></li>
            <li className="breadcrumb-item active">Dashboard</li>
          </ol>
        </nav>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        {stats.map((stat, index) => (
          <Col key={index} sm={6} lg={3} className="mb-3">
            <Link to={stat.link} className="text-decoration-none">
              <Card className="stat-card h-100 hover-shadow">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1">{stat.title}</p>
                      <h3 className="mb-0 fw-bold text-dark">{stat.value}</h3>
                      {stat.subtitle && (
                        <small className="text-muted">{stat.subtitle}</small>
                      )}
                    </div>
                    <div className={`stat-icon bg-${stat.color}-subtle text-${stat.color} rounded-circle p-2`}>
                      {stat.icon}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      <Row>
        {/* Recent Cases */}
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center bg-white">
              <h5 className="mb-0"><FiBriefcase className="me-2" />Casos Recientes</h5>
              <Link to="/cases" className="btn btn-sm btn-outline-primary">Ver todos</Link>
            </Card.Header>
            <Card.Body className="p-0">
              {recentCases.length === 0 ? (
                <div className="text-center py-4 text-muted">No hay casos recientes</div>
              ) : (
                <Table hover responsive className="mb-0 table-fixed">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th style={{ width: '35%' }}>Caso</th>
                      <th style={{ width: '35%' }}>Cliente</th>
                      <th style={{ width: '20%' }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCases.map(c => (
                      <tr key={c.id}>
                        <td className="text-center">{getSemaphoreBadge(c.semaphore_color)}</td>
                        <td style={{ maxWidth: '150px' }}>
                          <Link 
                            to={`/cases/${c.id}`} 
                            className="text-truncate d-block" 
                            title={c.case_number}
                          >
                            {c.case_number}
                          </Link>
                        </td>
                        <td className="text-truncate" style={{ maxWidth: '150px' }} title={c.client_name}>
                          {c.client_name}
                        </td>
                        <td>{getStatusBadge(c.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Pending Tasks */}
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center bg-white">
              <h5 className="mb-0"><FiCheckSquare className="me-2" />Tareas Pendientes</h5>
              <Link to="/tasks" className="btn btn-sm btn-outline-primary">Ver todas</Link>
            </Card.Header>
            <Card.Body className="p-0">
              {pendingTasks.length === 0 ? (
                <div className="text-center py-4 text-muted">No hay tareas pendientes</div>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th></th>
                      <th>Tarea</th>
                      <th>Prioridad</th>
                      <th>Vence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingTasks.map(t => (
                      <tr key={t.id}>
                        <td className="text-center">{getSemaphoreBadge(t.semaphore)}</td>
                        <td>
                          <div>{t.title}</div>
                          {t.case_number && (
                            <small className="text-muted">{t.case_number}</small>
                          )}
                        </td>
                        <td>{getPriorityBadge(t.priority)}</td>
                        <td>
                          <FiClock className="me-1" />
                          {formatDate(t.due_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Upcoming Hearings */}
      <Row>
        <Col lg={12} className="mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center bg-white">
              <h5 className="mb-0"><FiCalendar className="me-2" />Próximas Audiencias (7 días)</h5>
              <Link to="/hearings" className="btn btn-sm btn-outline-primary">Ver todas</Link>
            </Card.Header>
            <Card.Body className="p-0">
              {upcomingHearings.length === 0 ? (
                <div className="text-center py-4 text-muted">No hay audiencias programadas</div>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Hora</th>
                      <th>Audiencia</th>
                      <th>Caso</th>
                      <th>Lugar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingHearings.map(h => {
                      const isToday = new Date(h.scheduled_date).toDateString() === new Date().toDateString();
                      return (
                        <tr key={h.id} className={isToday ? 'table-warning' : ''}>
                          <td>
                            <strong>{formatDate(h.scheduled_date)}</strong>
                            {isToday && <Badge bg="warning" className="ms-2">HOY</Badge>}
                          </td>
                          <td>{h.scheduled_time?.substring(0, 5) || '-'}</td>
                          <td>{h.title}</td>
                          <td>
                            {h.case_number ? (
                              <Link to={`/cases/${h.case_id}`}>{h.case_number}</Link>
                            ) : '-'}
                          </td>
                          <td>{h.location || h.virtual_link ? 'Virtual' : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
